import type { VisualizationTypeDefinition } from '../models/chart.types'
import type { ReportColumn } from '../models/column.types'
import type { ReportRow } from '../models/report.types'
import {
  IMPLEMENTED_REPORT_TABLE_TYPES,
  type ReportTableType,
} from '../models/tablePresentation.types'

import {
  isAdditiveReportMetric,
  resolveMatrixPivotAssignment,
  type MatrixPresentationOptions,
} from './tablePivot'

export {
  isAdditiveReportMetric,
  resolveMatrixPivotAssignment,
  resolveTrendAssignment,
  type MatrixPivotAssignment,
  type MatrixPresentationOptions,
  type ReportTableAvailabilityContext,
  type TrendAssignment,
  type TrendPresentationOptions,
} from './tablePivot'

export type ReportHeatmapIntensity = 'low' | 'medium' | 'high'

export interface ReportHeatmapCellState {
  intensity: ReportHeatmapIntensity
  ratio: number
}

export interface MatrixPresentation {
  valid: boolean
  rows: ReportRow[]
  columns: ReportColumn[]
}

export interface ReportTotalSources {
  total?: Record<string, unknown> | null
  otherTotal?: Record<string, unknown> | null
  grandTotal?: Record<string, unknown> | null
  ga4Total?: Record<string, unknown> | null
}

export interface ReportTotalLabels {
  total: string
  otherTotal: string
  grandTotal: string
  ga4Total?: string
}

const LOCAL_TABLE_CONSTRAINTS: Record<
  (typeof IMPLEMENTED_REPORT_TABLE_TYPES)[number],
  { minDimensions: number; minMetrics: number }
> = {
  table: { minDimensions: 0, minMetrics: 0 },
  matrix_table: { minDimensions: 1, minMetrics: 1 },
  trend_table: { minDimensions: 1, minMetrics: 1 },
}

/** Backend capability intersected with the local presenter registry; absence means degraded mode. */
export function getAvailableReportTableTypes(
  definitions: readonly VisualizationTypeDefinition[] | null | undefined,
  dimensions: readonly string[],
  metrics: readonly string[],
): ReportTableType[] {
  const byType = new Map<string, VisualizationTypeDefinition>()
  for (const definition of definitions ?? []) {
    if (definition.kind !== 'table' || byType.has(definition.type)) continue
    byType.set(definition.type, definition)
  }

  return IMPLEMENTED_REPORT_TABLE_TYPES.filter((type) => {
    const local = LOCAL_TABLE_CONSTRAINTS[type]
    if (dimensions.length < local.minDimensions || metrics.length < local.minMetrics) return false
    // Matrix/Trend reshape already-fetched rows entirely client-side (a pivot, a date-bucket
    // regroup) — they are local presenters, not a distinct backend-computed visualization type,
    // so unlike every other table kind they need no backend catalog declaration to be offered.
    if (type === 'matrix_table' || type === 'trend_table') return true
    if (definitions === null || definitions === undefined) return true
    const definition = byType.get(type)
    // `field_roles` is a chart assignment contract. Backend table definitions legitimately omit
    // it, so table availability must be decided from table capabilities and local presenters.
    if (!definition || definition.isWidget) return false
    if (
      definition.requiresTimeDimension ||
      definition.requiresComparison ||
      (definition.requiresHierarchy && dimensions.length === 0) ||
      (definition.requiresPairs && dimensions.length < 2)
    )
      return false
    return (
      dimensions.length >= definition.minDimensions &&
      (definition.maxDimensions === null || dimensions.length <= definition.maxDimensions) &&
      metrics.length >= definition.minMetrics &&
      (definition.maxMetrics === null || metrics.length <= definition.maxMetrics)
    )
  })
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function displayKey(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

function firstDimension(
  columns: readonly ReportColumn[],
  excludedField?: string | null,
): ReportColumn | undefined {
  return columns.find(
    (column) =>
      !column.invisible && !column.numeric && !column.isImage && column.field !== excludedField,
  )
}

function isReportRow(value: unknown): value is ReportRow {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function controllerBreakdownRows(row: ReportRow): ReportRow[] {
  const edges = Array.isArray(row.edges) ? row.edges.filter(isReportRow) : []
  if (edges.length > 0) return edges
  const values = row['breakdown_values']
  return Array.isArray(values) ? values.filter(isReportRow) : []
}

function expandedRows(rows: readonly ReportRow[]): ReportRow[] {
  return rows.flatMap((row) => {
    const children = controllerBreakdownRows(row)
    if (children.length === 0) return [{ ...row }]
    const parent = { ...row }
    delete parent.edges
    delete parent['breakdown_values']
    return children.map((child) => ({ ...parent, ...child }))
  })
}

/** Pivot report rows without mutating controller-owned data; malformed configs fail closed. */
export function buildMatrixPresentation(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  options: MatrixPresentationOptions,
): MatrixPresentation {
  const assignment = resolveMatrixPivotAssignment(columns, options)
  if (!assignment) return { valid: false, rows: [], columns: [] }
  const breakdownField = assignment.breakdownField
  const matrixDimension = columns.find(
    (column) => column.field === assignment.matrixDimension,
  ) as ReportColumn
  const pivotMetric = columns.find(
    (column) => column.field === assignment.pivotMetric,
  ) as ReportColumn
  const additive = isAdditiveReportMetric(pivotMetric)

  const flattened = expandedRows(rows)
  const categoryFields = new Map<string, string>()
  const categoryOrder: string[] = []
  const grouped = new Map<string, ReportRow>()
  const cellStates = new Map<
    string,
    Map<string, { count: number; invalid: boolean; value: number }>
  >()

  for (const row of flattened) {
    const rowKey = displayKey(row[matrixDimension.field])
    const category = displayKey(row[breakdownField])
    let categoryField = categoryFields.get(category)
    if (!categoryField) {
      categoryField = `matrix_${categoryOrder.length}`
      categoryFields.set(category, categoryField)
      categoryOrder.push(category)
    }
    const current = grouped.get(rowKey) ?? { [matrixDimension.field]: row[matrixDimension.field] }
    const nextValue = finiteNumber(row[pivotMetric.field])
    const rowStates = cellStates.get(rowKey) ?? new Map()
    cellStates.set(rowKey, rowStates)
    const previous = rowStates.get(categoryField) ?? { count: 0, invalid: false, value: 0 }
    const state = {
      count: previous.count + 1,
      invalid: previous.invalid || nextValue === null || (!additive && previous.count > 0),
      value: previous.value + (nextValue ?? 0),
    }
    rowStates.set(categoryField, state)
    if (state.invalid) delete current[categoryField]
    else current[categoryField] = additive ? state.value : nextValue
    grouped.set(rowKey, current)
  }

  const matrixColumns: ReportColumn[] = [
    { ...matrixDimension, frozen: true },
    ...categoryOrder.map((category) => ({
      field: categoryFields.get(category) as string,
      header: category,
      numeric: true,
      ...(pivotMetric.sub_type === undefined ? {} : { sub_type: pivotMetric.sub_type }),
      sortable: false,
      resizable: true,
      meta: { matrixBreakdown: breakdownField, pivotMetric: pivotMetric.field },
    })),
  ]

  return { valid: true, rows: [...grouped.values()], columns: matrixColumns }
}

function groupFields(columns: readonly ReportColumn[], breakdownField: string): string[] {
  return columns
    .filter(
      (column) =>
        !column.invisible && !column.numeric && !column.isImage && column.field !== breakdownField,
    )
    .map((column) => column.field)
}

function breakdownChildFields(columns: readonly ReportColumn[], breakdownField: string): string[] {
  return columns
    .filter((column) => !column.invisible && !column.numeric && column.field !== breakdownField)
    .map((column) => column.field)
}

function cloneRowsWithBreakdowns(
  rows: readonly ReportRow[],
  childFields: readonly string[],
): ReportRow[] {
  return rows.map((row) => {
    const children = controllerBreakdownRows(row)
    const clone = { ...row }
    delete clone['breakdown_values']
    if (children.length === 0) return clone
    clone.edges = children.map((child) => ({
      ...Object.fromEntries(childFields.map((field) => [field, row[field]])),
      ...child,
    }))
    return clone
  })
}

function blankBreakdownParentStatus(row: ReportRow, breakdownField: string): ReportRow {
  const statusField =
    breakdownField === 'adgroup_name'
      ? 'adgroup_status'
      : breakdownField === 'campaign_name'
        ? 'campaign_status'
        : null
  return statusField ? { ...row, [statusField]: ' - ' } : row
}

function createBreakdownParent(
  row: ReportRow,
  dimensions: readonly string[],
  metrics: readonly ReportColumn[],
): ReportRow {
  const parent: ReportRow = { edges: [{ ...row }] }
  for (const field of dimensions) parent[field] = row[field]
  for (const metric of metrics) {
    const value = finiteNumber(row[metric.field])
    if (value !== null) parent[metric.field] = value
  }
  return parent
}

function appendBreakdownRow(
  parent: ReportRow,
  row: ReportRow,
  metrics: readonly ReportColumn[],
): void {
  parent.edges = [...(parent.edges ?? []), { ...row }]
  for (const metric of metrics) {
    const next = finiteNumber(row[metric.field])
    if (next !== null) parent[metric.field] = (finiteNumber(parent[metric.field]) ?? 0) + next
  }
}

/** Build expandable parents from flat rows or either controller nested-breakdown shape. */
export function buildBreakdownRows(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  breakdownField: string | null | undefined,
): ReportRow[] {
  if (!breakdownField) return rows.map((row) => ({ ...row }))
  if (rows.some((row) => controllerBreakdownRows(row).length > 0))
    return cloneRowsWithBreakdowns(rows, breakdownChildFields(columns, breakdownField)).map((row) =>
      blankBreakdownParentStatus(row, breakdownField),
    )

  const dimensions = groupFields(columns, breakdownField)
  if (dimensions.length === 0) return rows.map((row) => ({ ...row }))
  const metrics = columns.filter(isAdditiveReportMetric)
  const groups = new Map<string, ReportRow>()

  for (const row of rows) {
    const key = JSON.stringify(dimensions.map((field) => row[field]))
    const current = groups.get(key)
    if (current) {
      appendBreakdownRow(current, row, metrics)
      continue
    }
    groups.set(key, createBreakdownParent(row, dimensions, metrics))
  }

  return [...groups.values()].map((row) => blankBreakdownParentStatus(row, breakdownField))
}

function hasValues(
  value: Record<string, unknown> | null | undefined,
): value is Record<string, unknown> {
  return Boolean(value && Object.keys(value).length > 0)
}

function equalValues(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length && left.every((value, index) => equalValues(value, right[index]))
    )
  }
  if (
    typeof left === 'object' &&
    left !== null &&
    !Array.isArray(left) &&
    typeof right === 'object' &&
    right !== null &&
    !Array.isArray(right)
  ) {
    const leftRecord = left as Record<string, unknown>
    const rightRecord = right as Record<string, unknown>
    const keys = Object.keys(leftRecord)
    return (
      keys.length === Object.keys(rightRecord).length &&
      keys.every((key) => key in rightRecord && equalValues(leftRecord[key], rightRecord[key]))
    )
  }
  return false
}

/** Map controller total buckets into footer rows with a visible, caller-localised row label. */
export function buildReportTotalRows(
  columns: readonly ReportColumn[],
  sources: ReportTotalSources,
  labels: ReportTotalLabels,
): ReportRow[] {
  const labelField = firstDimension(columns)?.field ?? columns[0]?.field
  if (!labelField) return []
  const buckets: Array<[Record<string, unknown> | null | undefined, string]> = [
    [sources.total, labels.total],
    [sources.otherTotal, labels.otherTotal],
    [equalValues(sources.total, sources.grandTotal) ? null : sources.grandTotal, labels.grandTotal],
    [sources.ga4Total, labels.ga4Total ?? 'GA4 Total'],
  ]
  return buckets.flatMap(([bucket, label]) =>
    hasValues(bucket) ? [{ ...bucket, [labelField]: label }] : [],
  )
}

function intensityFor(ratio: number): ReportHeatmapIntensity {
  if (ratio >= 2 / 3) return 'high'
  if (ratio >= 1 / 3) return 'medium'
  return 'low'
}

/** Per-column heatmap normalization retains the visible numeric value as the primary evidence. */
export function buildHeatmapCells(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  fields?: readonly string[],
): Array<Record<string, ReportHeatmapCellState>> {
  const allowed = fields ? new Set(fields) : null
  const numericFields = columns
    .filter((column) => column.numeric && (!allowed || allowed.has(column.field)))
    .map((column) => column.field)
  const domains = new Map<string, { min: number; max: number }>()

  for (const field of numericFields) {
    const values = rows.flatMap((row) => {
      const value = finiteNumber(row[field])
      return value === null ? [] : [value]
    })
    if (values.length > 0)
      domains.set(field, { min: Math.min(...values), max: Math.max(...values) })
  }

  return rows.map((row) =>
    Object.fromEntries(
      numericFields.flatMap((field) => {
        const value = finiteNumber(row[field])
        const domain = domains.get(field)
        if (value === null || !domain) return []
        const ratio =
          domain.max === domain.min ? 1 : (value - domain.min) / (domain.max - domain.min)
        return [[field, { ratio, intensity: intensityFor(ratio) }] as const]
      }),
    ),
  )
}
